/**
 * Display formatting.
 *
 * Pakistani conventions throughout: Rs. before the amount, the South Asian
 * digit grouping (1,23,456 rather than 123,456) where it reads naturally, and
 * 12-hour time, which is how a shift is talked about.
 */

/** Two decimals with grouping: 1234.5 -> "1,234.50" */
export const money = (value, decimals = 2) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return (0).toFixed(decimals);
    return num.toLocaleString('en-PK', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

/** With the currency symbol: "Rs. 1,234.50" */
export const currency = (value, symbol = 'Rs.', decimals = 2) =>
    `${symbol} ${money(value, decimals)}`;

/**
 * Compact, for dashboard tiles where the exact rupee does not matter:
 * 1234567 -> "12.35 Lac". Lac and crore rather than M and B, because that is
 * how the number is read here.
 */
export const compactMoney = (value) => {
    const num = Number(value) || 0;
    const abs = Math.abs(num);

    if (abs >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
    if (abs >= 100000) return `${(num / 100000).toFixed(2)} Lac`;
    if (abs >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return money(num, 0);
};

/** Quantities: trailing zeros dropped, so 2.000 shows as 2 and 0.500 as 0.5 */
export const quantity = (value, maxDecimals = 3) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '0';
    return num.toLocaleString('en-PK', { maximumFractionDigits: maxDecimals });
};

export const percent = (value, decimals = 0) => `${Number(value || 0).toFixed(decimals)}%`;

/* ---------------------------------- dates --------------------------------- */

const toDate = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** DD-MM-YYYY, the format on the receipt. */
export const formatDate = (value) => {
    const date = toDate(value);
    if (!date) return '';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}-${mm}-${date.getFullYear()}`;
};

/** HH:MM in 24-hour, which is what the receipt prints. */
export const formatTime = (value) => {
    const date = toDate(value);
    if (!date) return '';
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

/** 12-hour with am/pm, for anything a person reads conversationally. */
export const formatTime12 = (value) => {
    const date = toDate(value);
    if (!date) return '';
    return date.toLocaleTimeString('en-PK', { hour: 'numeric', minute: '2-digit', hour12: true });
};

export const formatDateTime = (value) => {
    const date = toDate(value);
    if (!date) return '';
    return `${formatDate(date)} ${formatTime(date)}`;
};

/** For an <input type="date"> value. */
export const toInputDate = (value) => {
    const date = toDate(value) || new Date();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${mm}-${dd}`;
};

/** "3 min ago" - used on the floor plan to show how long a table has been sat. */
export const relativeTime = (value) => {
    const date = toDate(value);
    if (!date) return '';

    const seconds = Math.round((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'just now';

    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} min`;

    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hr`;

    return formatDate(date);
};

/** Minutes as "1h 20m" - how long a table has been occupied. */
export const duration = (minutes) => {
    const total = Math.max(0, Math.round(Number(minutes) || 0));
    if (total < 60) return `${total}m`;
    return `${Math.floor(total / 60)}h ${total % 60}m`;
};

/* --------------------------------- strings -------------------------------- */

export const initials = (name) =>
    String(name || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('');

export const truncate = (text, length = 40) => {
    const value = String(text ?? '');
    return value.length > length ? `${value.slice(0, length - 1)}…` : value;
};

/** 03001234567 -> 0300-1234567, the way a Pakistani mobile is written. */
export const formatMobile = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (digits.length === 11) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    return value || '';
};
